'use client';

import { useTranslation } from 'next-i18next';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  PlusCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { TailwindSpinner, Anchor, MissionControlSwitch } from '@components/common';
import { useSwrMcMyJobsSearch } from '@hooks/useSwrApi/endpoints/my-jobs/useSwrMcMyJobsSearch';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { MemoizedCompactJobCard } from './CompactJobCard';

const JOBS_PER_PAGE = 5;

interface CompanyJobsSidebarProps {
  locale: Locale;
  selectedJobSlug?: string;
  onJobSelect?: (job: ApiSchema<'McMyJobCard'>) => void;
  /** When provided, tag clicks call this instead of navigating to the standalone applicant page. */
  onTagClick?: (job: ApiSchema<'McMyJobCard'>, status: number) => void;
  className?: string;
  isAuthenticated?: boolean;
  showAccountSettings?: boolean;
  onSwitchContext?: () => void;
}

/**
 * @description Inner component that fetches and displays jobs.
 * Only rendered when user is authenticated.
 */
const CompanyJobsSidebarContent = (props: Omit<CompanyJobsSidebarProps, 'isAuthenticated'>) => {
  const {
    locale, selectedJobSlug, onJobSelect, onTagClick, className, showAccountSettings = true, onSwitchContext,
  } = props;
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const {
    data,
    isLoading,
    isEmpty,
  } = useSwrMcMyJobsSearch({
    locale,
    startIndex: 1,
    limit: JOBS_PER_PAGE,
    urlParams: '&sort=new',
  });

  // Flatten all jobs from paginated data
  const allJobs = data?.flatMap((page) => page?.items || []) || [];

  return (
    <div className={`flex h-full flex-col bg-surface ${className}`}>
      {/* Top content - scrollable */}
      <div className="scrollbar-hide flex-1 overflow-y-auto px-4 pb-4 pt-0">
        {/* Navigation Header - Company Links */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          {/* Switch Context - Segmented control: Hiring | Job search */}
          <MissionControlSwitch
            activeContext="company"
            hireLabel={t('mc.switch.hire')}
            jobSearchLabel={t('mc.switch.job.search')}
            onSwitch={onSwitchContext ?? (() => {})}
          />

          {/* New Project - Action TBD */}
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                window.dispatchEvent(new Event('close-sidebar'));
                window.dispatchEvent(new Event('close-detail-modal'));
              }
              void router.push('/mc/job-add/edit');
            }}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-200"
            data-test-id="sidebar-new-project"
          >
            <PlusCircleIcon className="size-5 text-ink" />
            <span>{t('header.label.new.job') || 'New job'}</span>
          </button>
        </div>

        {/* Company Jobs Section */}
        {!isEmpty && !isLoading && (
          <div className="mb-2 flex items-center justify-between px-3">
            <h3 className="text-sm font-bold text-ink">
              {t('my-jobs.page.title') || 'My Jobs'}
            </h3>
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col pb-4">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <TailwindSpinner className="size-6 text-primary" />
            </div>
          )}

          {/* Jobs list */}
          {!isEmpty && (
            <div className="flex flex-col gap-3">
              {allJobs.map((job) => (
                <MemoizedCompactJobCard
                  key={job.id}
                  job={job}
                  locale={locale}
                  isSelected={job.slug === selectedJobSlug}
                  onSelect={onJobSelect}
                  onTagClick={onTagClick}
                  buildTagHref={(slug) => getI18nLinkHref(`/mc/job/${slug}/applicants/flow`, locale)}
                />
              ))}
            </div>
          )}

          {/* See all jobs link */}
          {!isEmpty && !isLoading && (
            <div className="mt-6 flex justify-center">
              <Anchor
                href={getI18nLinkHref('/my-jobs', locale)}
                styling="none"
                className="flex items-center gap-1.5 text-base font-semibold text-primary hover:text-primary-light"
              >
                <span>{t('homepage.react.hero.heading.button.all-jobs') || 'My Jobs'}</span>
                <ArrowRightIcon className="size-4" />
              </Anchor>
            </div>
          )}
        </div>
      </div>

      {/* Account Settings Link - Bottom aligned, outside scroll area */}
      {showAccountSettings && (
        <div className="shrink-0 border-t border-input bg-surface px-4 py-3">
          <Anchor
            href="/account/settings"
            styling="none"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-200"
          >
            <Cog6ToothIcon className="size-5 text-ink" />
            <span>{t('account.settings.title') || 'Account Settings'}</span>
          </Anchor>
        </div>
      )}
    </div>
  );
};

/**
 * @description Modular company jobs list sidebar component.
 * Displays company jobs with "See more" pagination (10 jobs per page).
 * Shows a sign-in prompt when user is not authenticated.
 * Can be included in different pages like CV detail or hire landing.
 */
export const CompanyJobsSidebar = (props: CompanyJobsSidebarProps) => {
  const { isAuthenticated = false, ...restProps } = props;
  const { t } = useTranslation('common');

  // Show authenticated content if user is logged in
  if (isAuthenticated) {
    return <CompanyJobsSidebarContent {...restProps} />;
  }

  // Show sign-in prompt if user is not authenticated
  return (
    <div className={`h-full bg-surface ${restProps.className || ''}`}>
      <div className="scrollbar-hide h-full overflow-y-auto px-4 pt-4">
        {/* Navigation links */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          {/* Switch Context - Segmented control: Hiring | Job search */}
          <MissionControlSwitch
            activeContext="company"
            hireLabel={t('mc.switch.hire')}
            jobSearchLabel={t('mc.switch.job.search')}
            onSwitch={restProps.onSwitchContext ?? (() => {})}
          />
          {/* Sign In Link */}
          <Anchor
            href={`${getI18nLinkHref('/login', restProps.locale)}?_target_path=${encodeURIComponent(getI18nLinkHref('/mc', restProps.locale))}`}
            styling="none"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-200"
          >
            <UserCircleIcon className="size-5 text-primary" />
            <span>{t('menu.sign_in-register') || 'Sign In'}</span>
          </Anchor>
        </div>
      </div>
    </div>
  );
};
